/**
 * Auth helpers for the chatbot UI.
 *
 * The frontend NEVER holds an api_key or JWT in JS. Authentication is via
 * Cognito Hosted UI:
 *
 *   1. /auth/login        302 → Cognito Hosted UI
 *   2. user signs in      → Cognito → /auth/callback?code=…
 *   3. backend exchanges  → Set-Cookie: id_token=<JWT>; HttpOnly; Secure
 *   4. browser keeps the cookie automatically; JS in this tab CANNOT read it
 *      (HttpOnly), so XSS can't steal it and a security scanner crawling
 *      the deployed JS finds nothing sensitive.
 *
 * This module exposes:
 *   - whoami()    GET /auth/me (cookie-authenticated) → claims or null on 401
 *   - login()     redirects to /auth/login
 *   - logout()    POSTs /auth/logout (clears cookies + Cognito sign-out)
 *   - getWebSocketIdToken() asks /auth/me/token for a short-lived id_token
 *     to pass on the WebSocket query string (cookies don't travel to the
 *     separate ws-api domain).
 */

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || 'http://localhost:8080';

const listeners = new Set();
const notify = () => listeners.forEach((cb) => cb());

export function onAuthChange(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

const apiUrl = (path) => `${API_ENDPOINT.replace(/\/$/, '')}${path}`;

/**
 * GET /auth/me — returns claims if the cookie is valid, null otherwise.
 * Cookie is sent automatically because credentials: 'include'.
 */
export async function whoami() {
  try {
    const res = await fetch(apiUrl('/auth/me'), {
      method: 'GET',
      credentials: 'include',
    });
    if (res.ok) return await res.json();
    return null;
  } catch {
    return null;
  }
}

/**
 * Redirect to the backend login bouncer, which 302s to the Cognito
 * Hosted UI. After successful sign-in, the user lands back on the SPA
 * with the auth cookie set.
 */
export function login() {
  window.location.assign(apiUrl('/auth/login'));
}

/**
 * Sign out.
 *
 * POST /auth/logout clears the auth cookies on this same response and
 * returns the Cognito Hosted-UI logout URL. The browser then navigates
 * there to also clear Cognito's own session cookie before bouncing the
 * user back to the SPA, which on next bootstrap sees /auth/me 401 and
 * renders the Login page.
 *
 * POST-only (not GET) so that a malicious link / image can't trigger a
 * silent logout via CSRF.
 */
export async function logout() {
  let cognitoLogoutUrl = null;
  try {
    const res = await fetch(apiUrl('/auth/logout'), {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) {
      const body = await res.json();
      cognitoLogoutUrl = body && body.cognito_logout_url;
    }
  } catch {
    // network failure — still flip local state so the user sees the
    // login screen, even if cookies couldn't be cleared server-side.
  }
  notify();
  // Hard navigate so any in-memory React state is dropped. Prefer the
  // Cognito logout URL (clears the IdP session); fall back to the SPA
  // root, which will rebound through Login.
  window.location.assign(cognitoLogoutUrl || '/');
}
